import { IQrBarcodeModel, IQrLabelSize } from '../models/qr-label.model';

declare var Stimulsoft: any;

/**
 * Layout builder for Stimulsoft Avery and Zebra QR label reports.
 */
export class QrReportLayoutBuilder {
  /**
   * Builds Avery sheet layout (multi-column, multi-row with start offset support).
   */
  public static buildAveryLayout(
    page: any,
    barcodeValues: any[],
    labelSize: IQrLabelSize,
    model: IQrBarcodeModel
  ): void {
    const columns = labelSize.columns || 3;
    const width = labelSize.width || 2.625;
    const height = labelSize.height || 1.0;
    const horizontalPitch = labelSize.horizontalPitch || width;

    const startRow = Math.max(1, model?.Row || 1);
    const startCol = Math.max(1, model?.Column || 1);
    const skipCount = (startRow - 1) * columns + (startCol - 1);

    const paddedValues: any[] = [];
    for (let k = 0; k < skipCount; k++) {
      paddedValues.push(null);
    }
    paddedValues.push(...barcodeValues);

    let currentDataBand: any = null;
    let colIndex = 0;
    let rowIndex = 0;

    for (let i = 0; i < paddedValues.length; i++) {
      const item = paddedValues[i];

      if (colIndex === 0) {
        currentDataBand = new Stimulsoft.Report.Components.StiDataBand(
          new Stimulsoft.Base.Drawing.RectangleD(0, 0, width * columns, height)
        );
        currentDataBand.name = `DataBandRow${rowIndex}`;
        currentDataBand.height = height;
        page.components.add(currentDataBand);
      }

      if (item) {
        const posX = colIndex * horizontalPitch;
        const posY = 0;
        const qrValue = item.BarcodeNumber || item.barcodeNumber || item.code || item.id || '';
        const displayLabel = item.displayName || item.name || qrValue;

        this.addQrCodeComponent(currentDataBand, String(qrValue), String(displayLabel), posX, posY, width, height);
      }

      colIndex++;
      if (colIndex >= columns) {
        colIndex = 0;
        rowIndex++;
      }
    }
  }

  /**
   * Builds Zebra continuous roll layout (single label per band/page).
   */
  public static buildZebraLayout(
    page: any,
    barcodeValues: any[],
    labelSize: IQrLabelSize
  ): void {
    const width = labelSize.width || 2.0;
    const height = labelSize.height || 1.0;

    page.pageWidth = width;
    page.pageHeight = height;
    page.margins = new Stimulsoft.Report.Components.StiMargins(0, 0, 0, 0);

    for (let i = 0; i < barcodeValues.length; i++) {
      const item = barcodeValues[i];
      if (!item) continue;

      const dataBand = new Stimulsoft.Report.Components.StiDataBand(
        new Stimulsoft.Base.Drawing.RectangleD(0, 0, width, height)
      );
      dataBand.name = `DataBandZebra${i}`;
      dataBand.height = height;
      page.components.add(dataBand);

      const qrValue = item.BarcodeNumber || item.barcodeNumber || item.code || item.id || '';
      const displayLabel = item.displayName || item.name || qrValue;

      this.addQrCodeComponent(dataBand, String(qrValue), String(displayLabel), 0, 0, width, height);
    }
  }

  /**
   * Adds QR code component along with wrapped caption text to the container data band.
   */
  public static addQrCodeComponent(
    container: any,
    qrValue: string,
    displayLabel: string,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const hasText = !!(displayLabel && displayLabel.trim());
    const padding = 0.04;

    if (hasText) {
      const textH = Math.min(0.2, Math.max(0.14, h * 0.2));
      const gap = 0.02;
      const availH = Math.max(0.1, h - textH - gap - 2 * padding);
      const availW = Math.max(0.1, w - 2 * padding);
      const qrSize = Math.min(availW, availH);

      const blockH = qrSize + gap + textH;
      const startY = y + Math.max(padding, (h - blockH) / 2);
      const qrX = x + (w - qrSize) / 2;
      const qrY = startY;

      const qrBarcode = new Stimulsoft.Report.BarCodes.StiBarCode(
        new Stimulsoft.Base.Drawing.RectangleD(qrX, qrY, qrSize, qrSize)
      );
      qrBarcode.clientRectangle = new Stimulsoft.Base.Drawing.RectangleD(qrX, qrY, qrSize, qrSize);
      qrBarcode.codeValue = qrValue;
      qrBarcode.code = qrValue;
      qrBarcode.autoScale = true;
      qrBarcode.showLabelText = false;
      qrBarcode.barCodeType = new Stimulsoft.Report.BarCodes.StiQRCodeBarCodeType();
      qrBarcode.horAlignment = Stimulsoft.Base.Drawing.StiHorAlignment.Center;
      qrBarcode.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
      container.components.add(qrBarcode);

      const textX = x + padding;
      const textW = Math.max(0.1, w - 2 * padding);
      const textY = qrY + qrSize + gap;

      const textComponent = new Stimulsoft.Report.Components.StiText(
        new Stimulsoft.Base.Drawing.RectangleD(textX, textY, textW, textH)
      );
      textComponent.clientRectangle = new Stimulsoft.Base.Drawing.RectangleD(textX, textY, textW, textH);
      textComponent.text = displayLabel;
      textComponent.font = new Stimulsoft.System.Drawing.Font('Arial', 8.5, Stimulsoft.System.Drawing.FontStyle.Bold);
      textComponent.horAlignment = Stimulsoft.Base.Drawing.StiTextHorAlignment.Center;
      textComponent.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
      textComponent.wordWrap = true;
      container.components.add(textComponent);
    } else {
      const qrSize = Math.min(w - 2 * padding, h - 2 * padding);
      const qrX = x + (w - qrSize) / 2;
      const qrY = y + (h - qrSize) / 2;

      const qrBarcode = new Stimulsoft.Report.BarCodes.StiBarCode(
        new Stimulsoft.Base.Drawing.RectangleD(qrX, qrY, qrSize, qrSize)
      );
      qrBarcode.clientRectangle = new Stimulsoft.Base.Drawing.RectangleD(qrX, qrY, qrSize, qrSize);
      qrBarcode.codeValue = qrValue;
      qrBarcode.code = qrValue;
      qrBarcode.autoScale = true;
      qrBarcode.showLabelText = false;
      qrBarcode.barCodeType = new Stimulsoft.Report.BarCodes.StiQRCodeBarCodeType();
      qrBarcode.horAlignment = Stimulsoft.Base.Drawing.StiHorAlignment.Center;
      qrBarcode.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
      container.components.add(qrBarcode);
    }
  }
}
